/**
 * Shared contract for the 카톡 성적표 (KakaoTalk report card) tool.
 *
 * Every module (parser, stats, report, intake, UI) codes against these types.
 * Change them only together with docs/kakao-report-card-spec.md.
 *
 * Time model: KakaoTalk exports carry local wall-clock times without a zone.
 * We keep them as "wall-clock ms": Date.UTC(year, month - 1, day, hour, minute).
 * Always read them back with UTC getters (getUTCHours, getUTCDay, ...), never
 * with local-time getters, so results never depend on the viewer's time zone.
 */

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

/** Line grammars we recognise. See docs/kakao-report-card-spec.md §3. */
export type KakaoExportFormat =
  | 'pc-bracket' //          [이름] [오후 3:12] 본문 (PC Windows / Mac)
  | 'mobile-date-first' //   2024년 3월 1일 오후 3:12, 이름 : 본문
  | 'mobile-dot-date' //     2024. 3. 1. 오후 3:12, 이름 : 본문
  | 'legacy-time-first' //   오후 10:50, 2011년 11월 6일, 이름 : 본문
  | 'mobile-en' //           February 10, 2016, 2:33 AM, 이름 : 본문
  | 'csv'; //                legacy PC/Mac CSV (Date,User,Message)

/** Order matters: ParsedChat.kind stores the index into this list. */
export const MESSAGE_KINDS = [
  'text',
  'photo',
  'video',
  'emoticon',
  'voice',
  'file',
  'call',
  'transfer',
  'deleted',
  'hidden',
  'media',
] as const;
export type MessageKind = (typeof MESSAGE_KINDS)[number];

export type ParseWarningCode =
  /** PC exports only contain the messages loaded in the chat window. */
  | 'PC_EXPORT_MAY_BE_PARTIAL'
  /** Multi-part export where part numbers are not contiguous from 1, or a numbered part could not be decoded. */
  | 'MULTIPART_MISSING_PARTS'
  /** Some non-empty lines could not be attributed to a message. */
  | 'UNPARSED_LINES'
  /** More than MAX_MESSAGES messages: only the most recent ones were kept. */
  | 'TRIMMED_TO_RECENT'
  /** A sender label such as '회원님' / 'you' that usually marks the exporter. */
  | 'EXPORTER_LABEL_DETECTED';

export interface ParseWarning {
  readonly code: ParseWarningCode;
  /** Optional count (lines, dropped messages, ...). */
  readonly count?: number;
}

export type KakaoParseErrorCode =
  | 'NO_FILES'
  | 'UNSUPPORTED_FILE' // not .txt / .zip / .csv, or an image/video/etc.
  | 'FILE_TOO_LARGE'
  | 'ZIP_CORRUPT'
  | 'ZIP_NO_TEXT' // zip without a chat .txt inside
  | 'ENCODING_UNREADABLE'
  | 'NOT_KAKAO_EXPORT'
  | 'EMPTY_EXPORT' // header present, no messages
  | 'TOO_FEW_MESSAGES'
  | 'SINGLE_SENDER'
  | 'DIFFERENT_CHATS'; // several files that clearly belong to different chats

/**
 * Columnar parse result. Index i across at/sender/kind/text describes one
 * message, in chronological order (stable for equal times). System lines
 * (joins, leaves, invites, date dividers, admin notices) are NOT messages.
 */
export interface ParsedChat {
  readonly format: KakaoExportFormat;
  /** Room title from the header, e.g. "서진 님과 카카오톡 대화" -> "서진". */
  readonly title: string | null;
  /** 저장한 날짜 as wall-clock ms, when present. */
  readonly savedAt: number | null;
  /** Unique raw sender labels; the index is the senderId. */
  readonly senders: readonly string[];
  /** Wall-clock ms per message (see time model above). Non-decreasing. */
  readonly at: Float64Array;
  /** senderId per message. */
  readonly sender: Uint32Array;
  /** Index into MESSAGE_KINDS per message. */
  readonly kind: Uint8Array;
  /** Message body; multi-line bodies are joined with "\n". Media keeps its placeholder text. */
  readonly text: readonly string[];
  readonly systemLineCount: number;
  readonly warnings: readonly ParseWarning[];
  /** File names that contributed messages, in merge order. */
  readonly sourceFiles: readonly string[];
}

export interface ParseProgress {
  readonly phase: 'reading' | 'unzipping' | 'parsing';
  readonly loadedBytes: number;
  readonly totalBytes: number;
}

export interface ParticipantSummary {
  readonly senderId: number;
  readonly rawName: string;
  readonly messageCount: number;
  readonly firstAt: number;
  readonly lastAt: number;
  /** True for labels such as '회원님' or 'you' that usually mean the exporter. */
  readonly exporterHint: boolean;
}

export interface ParseSummary {
  readonly format: KakaoExportFormat;
  readonly title: string | null;
  readonly savedAt: number | null;
  readonly messageCount: number;
  readonly systemLineCount: number;
  readonly firstAt: number;
  readonly lastAt: number;
  /** Sorted by messageCount, descending. */
  readonly participants: readonly ParticipantSummary[];
  readonly warnings: readonly ParseWarning[];
  readonly sourceFiles: readonly string[];
}

// ---------------------------------------------------------------------------
// Participant cleanup (user input between parsing and analysis)
// ---------------------------------------------------------------------------

export interface AnalysisPerson {
  /** Display name chosen by the user (trimmed, 1-20 chars). */
  readonly name: string;
  /** senderIds merged into this person. Every senderId belongs to exactly one person. */
  readonly members: readonly number[];
}

export interface AnalysisSetup {
  /** At least two people. */
  readonly people: readonly AnalysisPerson[];
  /** Index into people for "나", or null when the user skipped it. */
  readonly meIndex: number | null;
  /**
   * Room name from the name screen, pre-filled with the export title for
   * groups; null for pairs. It is the only source of ChatStats.title: null or
   * blank means no title, and the export title is never used as a fallback.
   */
  readonly title: string | null;
}

// ---------------------------------------------------------------------------
// Statistics (deterministic, computed in the browser)
// ---------------------------------------------------------------------------

export interface CountedItem {
  readonly label: string;
  readonly count: number;
}

export interface MediaCounts {
  readonly photo: number;
  readonly video: number;
  readonly emoticon: number;
  readonly voice: number;
  readonly file: number;
  readonly call: number;
  readonly transfer: number;
  readonly deleted: number;
  /** URLs found inside text messages. */
  readonly link: number;
}

export interface LaughStats {
  /** Text messages containing a ㅋ or ㅎ run of length >= 2. */
  readonly messages: number;
  /** messages / textMessages (0 when no text messages). */
  readonly rate: number;
  /** Average length of ㅋ runs (length >= 2); 0 when none. */
  readonly avgRun: number;
  /** Longest ㅋ run. */
  readonly maxRun: number;
  /** Total ㅋ characters inside runs of length >= 2. */
  readonly kCount: number;
  /** Total ㅎ characters inside runs of length >= 2. */
  readonly hCount: number;
  /** Runs of ㅠ/ㅜ with length >= 2. */
  readonly cryRuns: number;
}

export interface CatchphraseItem {
  readonly phrase: string;
  readonly count: number;
  /** This person's usage rate divided by everyone else's (smoothed). */
  readonly ratio: number;
}

export interface PersonStats {
  readonly index: number;
  readonly name: string;
  readonly isMe: boolean;
  /** All messages except system lines (text + media). */
  readonly messages: number;
  readonly share: number;
  readonly textMessages: number;
  /** Non-whitespace characters (code points) across text messages. */
  readonly characters: number;
  readonly avgTextLength: number;
  /** Text messages with <= SHORT_REPLY_MAX_CHARS non-whitespace characters. */
  readonly shortReplyRate: number;
  /** Text messages ending with '?' or '？' (after trimming). */
  readonly questionRate: number;
  /** Conversation starts (선톡). */
  readonly starts: number;
  readonly startShare: number;
  /** Turn-taking replies inside a conversation. */
  readonly replies: number;
  /** Median reply delay in minutes; null when fewer than MIN_REPLIES_FOR_SPEED replies. */
  readonly medianReplyMinutes: number | null;
  /** Share of replies with delay <= QUICK_REPLY_MINUTES; null like above. */
  readonly quickReplyRate: number | null;
  /** Consecutive messages before someone else speaks (inside a conversation). */
  readonly burst: { readonly average: number; readonly max: number; readonly maxAt: number | null };
  /** 24 buckets, local hour of day. */
  readonly hourly: readonly number[];
  /** Messages sent 00:00-04:59 / messages. */
  readonly lateNightRate: number;
  readonly media: MediaCounts;
  readonly laugh: LaughStats;
  /** Top emojis, most used first (only the first TOP_EMOJIS). */
  readonly emojis: readonly CountedItem[];
  /** Every emoji this person used across text messages, not only the top few. */
  readonly emojiCount: number;
  readonly catchphrases: readonly CatchphraseItem[];
  /** '사랑해'-family occurrences. */
  readonly love: number;
  /** Days on which this person sent the first morning greeting. */
  readonly morningFirsts: number;
}

export interface DayCount {
  /** YYYY-MM-DD (wall-clock). */
  readonly date: string;
  readonly count: number;
}

export interface MessageSnippet {
  readonly person: number;
  readonly at: number;
  readonly kind: MessageKind;
  /** Truncated to SNIPPET_MAX_CHARS with "…". */
  readonly text: string;
}

export interface ChatStatsTotals {
  readonly messages: number;
  readonly textMessages: number;
  readonly people: number;
  readonly firstAt: number;
  readonly lastAt: number;
  /** Calendar days from first to last message, inclusive. */
  readonly spanDays: number;
  readonly activeDays: number;
  readonly perDay: number;
  readonly perActiveDay: number;
  readonly conversations: number;
  readonly avgConversationMessages: number;
}

export interface ChatStats {
  readonly roomKind: 'pair' | 'group';
  /** setup.title trimmed, or null; when null, the cover and the share image use the names. */
  readonly title: string | null;
  readonly people: readonly PersonStats[];
  readonly totals: ChatStatsTotals;
  readonly hourly: readonly number[];
  /** 7 buckets, 0 = Sunday. */
  readonly weekday: readonly number[];
  readonly busiestHour: number;
  readonly lateNightRate: number;
  /** Active days only, ascending by date. */
  readonly calendar: readonly DayCount[];
  readonly recordDay: DayCount | null;
  readonly longestStreak: { readonly days: number; readonly from: string; readonly to: string } | null;
  readonly longestSilence: { readonly minutes: number; readonly from: number; readonly to: number } | null;
  readonly topEmojis: readonly CountedItem[];
  readonly topWords: readonly CountedItem[];
  readonly love: {
    readonly total: number;
    readonly first: { readonly at: number; readonly person: number } | null;
    readonly last: { readonly at: number; readonly person: number } | null;
  };
  readonly morning: { readonly days: number };
  readonly longestMessage: { readonly person: number; readonly at: number; readonly length: number; readonly excerpt: string } | null;
  readonly firstMessages: readonly MessageSnippet[];
  readonly lastMessages: readonly MessageSnippet[];
  /** Copied from PARSE warnings so the UI can explain limits. */
  readonly warnings: readonly ParseWarning[];
}

// ---------------------------------------------------------------------------
// Report card (rule-based copy, no AI)
// ---------------------------------------------------------------------------

export type Grade = 'A+' | 'A' | 'B' | 'C' | 'D';

export type SubjectId =
  | 'initiative' // 선톡 성실도
  | 'reply-speed' // 칼답 속도
  | 'night-owl' // 새벽 감성
  | 'laughter' // 웃음 지수
  | 'expression' // 표현력 (이모지·이모티콘)
  | 'long-form'; // 장문력

export interface SubjectScore {
  readonly person: number;
  readonly grade: Grade;
  /** Human label of the value behind the grade, e.g. "선톡 73%", "보통 2분". */
  readonly valueLabel: string;
}

export interface ReportSubject {
  readonly id: SubjectId;
  readonly title: string;
  /**
   * One score per person with enough data, in person order (match by `person`).
   * Empty when nobody has enough data.
   */
  readonly scores: readonly SubjectScore[];
  /** 담임 한마디 for this subject. */
  readonly comment: string;
}

export interface Award {
  readonly id: string;
  /** e.g. "선톡 반장" */
  readonly title: string;
  readonly person: number;
  /** e.g. "대화의 72%를 먼저 열었어요" */
  readonly reason: string;
}

export interface RoastLine {
  readonly id: string;
  readonly text: string;
  /** Higher = more striking. Lines are sorted by weight, descending. */
  readonly weight: number;
  readonly people: readonly number[];
}

export interface ReportCard {
  /** 담임 총평: one sentence. May quote a word from the chat (말버릇). */
  readonly headline: string;
  /**
   * 담임 총평 for the share image: roles and room facts only, never a word
   * quoted from the chat (no 말버릇, emoji or message text).
   */
  readonly shareHeadline: string;
  readonly subjects: readonly ReportSubject[];
  readonly awards: readonly Award[];
  readonly lines: readonly RoastLine[];
}

export interface ChatReport {
  readonly stats: ChatStats;
  readonly card: ReportCard;
}

// ---------------------------------------------------------------------------
// Email intake (mobile KakaoTalk "이메일로 보내기")
// ---------------------------------------------------------------------------

export interface IntakeSessionCreated {
  /** 10 chars from INTAKE_ID_ALPHABET; also the local part of the address. */
  readonly id: string;
  /** 32 lowercase hex chars. Sent only as "Authorization: Bearer <secret>". */
  readonly secret: string;
  /** `${id}@${domain}` */
  readonly address: string;
  /** Epoch ms on the server clock (real time, not wall-clock). */
  readonly expiresAt: number;
  /**
   * Milliseconds left until expiry, measured by the server when it sent the
   * response. Clients derive their local deadline from this, so a phone whose
   * clock is wrong still counts down correctly.
   */
  readonly expiresInMs: number;
}

export type IntakeState = 'waiting' | 'received' | 'expired';

export type IntakeFailure =
  | 'NO_ATTACHMENT' // e-mail arrived without a chat file
  | 'LINK_ONLY' // large-file link (Drive, Naver, iCloud) instead of an attachment
  | 'TOO_LARGE'
  | 'UNSUPPORTED_ATTACHMENT' // attachments, but none is .txt/.zip/.csv
  | 'MAIL_UNREADABLE'; // the MIME structure could not be read (past its limits, malformed, timed out): a file may be attached

export interface IntakeFileInfo {
  readonly name: string;
  readonly size: number;
}

export interface IntakeStatus {
  readonly state: IntakeState;
  /** Epoch ms on the server clock. */
  readonly expiresAt: number;
  /** Milliseconds left, measured by the server (see IntakeSessionCreated.expiresInMs). */
  readonly expiresInMs: number;
  /** Present when state === 'received'. */
  readonly files?: readonly IntakeFileInfo[];
  /** Last rejected e-mail while still waiting (the page keeps waiting). */
  readonly lastFailure?: IntakeFailure;
}

export const INTAKE_API_BASE = '/api/kakao-intake/';
export const INTAKE_ID_ALPHABET = 'abcdefghijkmnpqrstuvwxyz23456789';
